import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, ChevronLeft, ChevronRight, QrCode, CreditCard, Barcode, MessageCircle, Loader2, PartyPopper, Copy } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, CATEGORIES } from "@/data/content";
import { api, formatBRL, whatsappLink, formatApiErrorDetail } from "@/lib/api";

const STEPS = ["Serviço", "Seus dados", "Detalhes", "Pagamento", "Confirmação"];
const PAYMENTS = [
  { id: "pix", label: "Pix", icon: QrCode, note: "Desconto + aprovação imediata", badge: "Recomendado" },
  { id: "cartao", label: "Cartão de Crédito", icon: CreditCard, note: "Em até 12x", badge: "Até 12x" },
  { id: "boleto", label: "Boleto Bancário", icon: Barcode, note: "Vence em até 3 dias úteis", badge: null },
];

export default function RequestWizard({ open, onClose, initialService, services = SERVICES, pixDiscount = 10 }) {
  const [step, setStep] = useState(0);
  const [service, setService] = useState(initialService || null);
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", cpf: "", notes: "" });
  const [payment, setPayment] = useState("pix");
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [loadingPix, setLoadingPix] = useState(false);
  const [pixSeconds, setPixSeconds] = useState(900);

  useEffect(() => {
    if (open) {
      setStep(initialService ? 1 : 0);
      setService(initialService || null);
      setOrder(null);
      setPixData(null);
      setForm({ name: "", whatsapp: "", email: "", cpf: "", notes: "" });
      setPayment("pix");
    }
  }, [open, initialService]);

  useEffect(() => {
    if (step === 4 && order && order.payment_method === "pix") {
      setLoadingPix(true);
      api.get(`/payments/pix/${order.id}`).then(({ data }) => setPixData(data)).catch(() => {}).finally(() => setLoadingPix(false));
    }
  }, [step, order]);

  useEffect(() => {
    if (!(step === 4 && order?.payment_method === "pix" && pixData)) return;
    setPixSeconds((pixData.expiration_minutes || 15) * 60);
    const t = setInterval(() => setPixSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, order, pixData]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canNext = () => {
    if (step === 0) return !!service;
    if (step === 1) return form.name && form.whatsapp && form.email && form.cpf;
    return true;
  };

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        name: form.name,
        whatsapp: form.whatsapp,
        email: form.email,
        cpf: form.cpf,
        service_id: service.id,
        payment_method: payment,
        notes: form.notes,
      });
      setOrder(data);
      setStep(4);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        order_id: order.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPaying(false);
    }
  };

  const regeneratePix = () => {
    setLoadingPix(true);
    api.get(`/payments/pix/${order.id}`).then(({ data }) => setPixData({ ...data })).catch(() => {}).finally(() => setLoadingPix(false));
  };

  const waMessage = order
    ? `Olá! Preciso de ajuda com ${order.service_name}. Protocolo: ${order.protocol}. Meu nome é ${order.name}.`
    : "";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="request-wizard" className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">Solicitação de Serviço</DialogTitle>
        <DialogDescription className="sr-only">Formulário em etapas para solicitar um serviço de assessoria.</DialogDescription>
        {/* Stepper */}
        <div className="bg-[#0F294A] px-6 py-5">
          <h2 className="font-heading font-bold text-white text-lg">Solicitação de Serviço</h2>
          <div className="flex items-center gap-1.5 mt-4">
            {STEPS.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-emerald-400" : "bg-white/20"}`} />
                <p className={`text-[10px] mt-1.5 hidden sm:block ${i <= step ? "text-emerald-300" : "text-white/40"}`}>{s}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 0 - service */}
          {step === 0 && (
            <div data-testid="wizard-step-service">
              <h3 className="font-heading font-bold text-slate-900 mb-1">Qual serviço você precisa?</h3>
              <p className="text-sm text-slate-500 mb-4">Selecione uma das opções abaixo.</p>
              <div className="space-y-4 max-h-[46vh] overflow-y-auto pr-1">
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <div key={key}>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{cat.label}</p>
                    <div className="grid gap-2">
                      {services.filter((s) => s.category === key).map((s) => (
                        <button
                          key={s.id}
                          data-testid={`wizard-service-${s.id}`}
                          onClick={() => setService(s)}
                          className={`flex items-center justify-between rounded-xl border p-3 text-left transition-all ${
                            service?.id === s.id ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <span className="text-sm font-medium text-slate-800">{s.name}</span>
                          <span className="text-sm font-bold text-slate-900 shrink-0 ml-3">{formatBRL(s.amount)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1 - personal */}
          {step === 1 && (
            <div data-testid="wizard-step-data" className="space-y-4">
              <h3 className="font-heading font-bold text-slate-900">Seus dados de contato</h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="w-name">Nome completo</Label>
                  <Input id="w-name" data-testid="wizard-input-name" value={form.name} onChange={set("name")} placeholder="Seu nome completo" className="mt-1.5" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="w-wpp">WhatsApp</Label>
                    <Input id="w-wpp" data-testid="wizard-input-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="(00) 00000-0000" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="w-cpf">CPF</Label>
                    <Input id="w-cpf" data-testid="wizard-input-cpf" value={form.cpf} onChange={set("cpf")} placeholder="000.000.000-00" className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="w-email">E-mail</Label>
                  <Input id="w-email" data-testid="wizard-input-email" type="email" value={form.email} onChange={set("email")} placeholder="voce@email.com" className="mt-1.5" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 - details */}
          {step === 2 && (
            <div data-testid="wizard-step-details" className="space-y-4">
              <h3 className="font-heading font-bold text-slate-900">Detalhes do pedido</h3>
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Serviço selecionado</p>
                <p className="font-semibold text-slate-900">{service?.name}</p>
                <p className="text-sm text-emerald-700 font-bold mt-1">{formatBRL(service?.amount || 0)}</p>
              </div>
              <div>
                <Label htmlFor="w-notes">Observações (opcional)</Label>
                <Textarea id="w-notes" data-testid="wizard-input-notes" value={form.notes} onChange={set("notes")} placeholder="Conte detalhes que ajudem no seu atendimento (nº do CNPJ, placa do veículo, etc.)" className="mt-1.5 min-h-[120px]" />
              </div>
            </div>
          )}

          {/* Step 3 - payment */}
          {step === 3 && (
            <div data-testid="wizard-step-payment" className="space-y-3">
              <h3 className="font-heading font-bold text-slate-900">Forma de pagamento</h3>
              <p className="text-sm text-slate-500">Escolha como prefere pagar os honorários de assessoria.</p>
              {service && (
                <div data-testid="wizard-pix-discount" className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between">
                  <span className="text-sm text-emerald-800 font-medium">{payment === "pix" ? `🎉 Com Pix você ganha ${pixDiscount}% de desconto!` : `Pagando no Pix você ganha ${pixDiscount}% de desconto`}</span>
                  <span className="font-heading font-extrabold text-emerald-700">{formatBRL(payment === "pix" ? service.amount * (1 - pixDiscount / 100) : service.amount)}</span>
                </div>
              )}
              {PAYMENTS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`wizard-payment-${p.id}`}
                  onClick={() => setPayment(p.id)}
                  className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-all ${
                    payment === p.id ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className={`grid place-items-center h-11 w-11 rounded-xl ${payment === p.id ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}><p.icon className="h-5 w-5" /></span>
                  <span className="flex-1">
                    <span className="font-semibold text-slate-900 flex items-center gap-2">{p.label} {p.badge && <span className="text-[10px] font-bold bg-emerald-600 text-white rounded-full px-2 py-0.5">{p.badge}</span>}</span>
                    <span className="block text-xs text-slate-500">{p.note}</span>
                  </span>
                  {payment === p.id && <Check className="h-5 w-5 text-emerald-600" />}
                </button>
              ))}
            </div>
          )}

          {/* Step 4 - confirmation */}
          {step === 4 && order && (
            <div data-testid="wizard-step-confirm" className="text-center py-2">
              <span className="grid place-items-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-4"><PartyPopper className="h-8 w-8" /></span>
              <h3 className="font-heading font-bold text-xl text-slate-900">Pedido registrado com sucesso!</h3>
              <p className="text-slate-500 mt-1 text-sm">Guarde seu número de protocolo para acompanhamento.</p>

              <div className="my-5 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-4 flex items-center justify-center gap-3">
                <div>
                  <p className="text-xs text-emerald-700 uppercase tracking-wide">Protocolo</p>
                  <p data-testid="order-protocol" className="font-heading font-extrabold text-2xl text-[#0F294A] tracking-wider">{order.protocol}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(order.protocol); toast.success("Protocolo copiado!"); }} className="grid place-items-center h-9 w-9 rounded-lg bg-white border border-emerald-300 text-emerald-700"><Copy className="h-4 w-4" /></button>
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-left text-sm space-y-1 mb-5">
                <div className="flex justify-between"><span className="text-slate-500">Serviço</span><span className="font-medium text-slate-900">{order.service_name}</span></div>
                {order.discount > 0 && (
                  <>
                    <div className="flex justify-between"><span className="text-slate-500">Valor</span><span className="text-slate-500 line-through">{formatBRL(order.amount)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Desconto Pix</span><span className="font-medium text-emerald-600">- {formatBRL(order.discount)}</span></div>
                  </>
                )}
                <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-bold text-slate-900">{formatBRL(order.final_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Pagamento</span><span className="font-medium text-slate-900 capitalize">{order.payment_method}</span></div>
              </div>

              <div className="flex flex-col gap-2.5">
                {order.payment_method === "pix" ? (
                  <div data-testid="pix-payment" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-sm font-semibold text-emerald-800 mb-3 flex items-center justify-center gap-2"><QrCode className="h-4 w-4" /> Pague com Pix e agilize seu atendimento</p>
                    {loadingPix || !pixData ? (
                      <div className="h-44 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
                    ) : (
                      <>
                        <div className={`bg-white rounded-xl p-3 w-fit mx-auto border border-emerald-100 ${pixSeconds === 0 ? "opacity-40" : ""}`}>
                          <img src={pixData.qr_base64} alt="QR Code Pix" data-testid="pix-qr-image" className="h-44 w-44" />
                        </div>
                        {pixSeconds > 0 ? (
                          <p className="text-center text-xs text-slate-500 mt-2" data-testid="pix-timer">Este código expira em <span className="font-bold text-[#0F294A]">{String(Math.floor(pixSeconds / 60)).padStart(2, "0")}:{String(pixSeconds % 60).padStart(2, "0")}</span></p>
                        ) : (
                          <div className="text-center mt-2">
                            <p className="text-sm font-semibold text-red-600">Código Pix expirado</p>
                            <Button data-testid="pix-regenerate" type="button" onClick={regeneratePix} variant="outline" className="mt-2 rounded-full">Gerar novo código</Button>
                          </div>
                        )}
                        <p className="text-center text-xs text-slate-500 mt-3">Escaneie o QR Code no app do seu banco ou copie o código Pix:</p>
                        <div className="mt-2 flex items-center gap-2">
                          <input readOnly value={pixData.payload} data-testid="pix-code" className="flex-1 min-w-0 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-600 truncate" />
                          <Button data-testid="pix-copy" type="button" onClick={() => { navigator.clipboard.writeText(pixData.payload); toast.success("Código Pix copiado!"); }} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg gap-1.5 shrink-0"><Copy className="h-4 w-4" /> Copiar</Button>
                        </div>
                        <p className="text-center text-sm font-bold text-[#0F294A] mt-3">Valor: {formatBRL(pixData.amount)}</p>
                        <a href={whatsappLink(`Olá! Acabei de pagar via Pix o serviço ${order.service_name} (Protocolo ${order.protocol}) no valor de ${formatBRL(order.final_amount)}. Segue o comprovante em anexo.`)} target="_blank" rel="noreferrer">
                          <Button data-testid="pix-paid-button" className="w-full mt-3 bg-[#0F294A] hover:bg-[#1E3A8A] text-white rounded-full h-11 gap-2"><MessageCircle className="h-4 w-4" /> Já paguei — enviar comprovante</Button>
                        </a>
                        <p className="text-center text-[11px] text-slate-400 mt-2">Pagamento direto via Pix para a nossa chave. Após pagar, toque no botão acima e anexe o comprovante no WhatsApp.</p>
                      </>
                    )}
                  </div>
                ) : (
                  <Button data-testid="wizard-pay-button" onClick={payNow} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-12 font-bold gap-2">
                    {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />} Pagar agora com segurança
                  </Button>
                )}
                <a href={whatsappLink(waMessage)} target="_blank" rel="noreferrer">
                  <Button data-testid="wizard-whatsapp-button" variant="outline" className="w-full rounded-full h-12 font-semibold border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-2">
                    <MessageCircle className="h-5 w-5" /> Enviar pedido no WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* Nav buttons */}
          {step < 4 && (
            <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100">
              <Button data-testid="wizard-back" variant="ghost" onClick={() => (step === 0 ? onClose() : setStep(step - 1))} className="text-slate-500 gap-1">
                <ChevronLeft className="h-4 w-4" /> {step === 0 ? "Cancelar" : "Voltar"}
              </Button>
              {step < 3 ? (
                <Button data-testid="wizard-next" onClick={() => setStep(step + 1)} disabled={!canNext()} className="bg-[#0F294A] hover:bg-[#1E3A8A] text-white rounded-full px-6 gap-1">
                  Continuar <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button data-testid="wizard-submit" onClick={submitOrder} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 gap-2">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirmar pedido
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
